import { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import { format, differenceInCalendarDays } from "date-fns";
import { Tab, Nav } from "react-bootstrap";
import { config, priceFormat, shortenUrl } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const ContractDetail = () => {
  const {id_contract} = useParams();
  const [contract, setContract] = useState(null);
  const [quotationMaster, setQuotationMaster] = useState(null);
  const [items, setItems] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [scheduleQuotationsMap, setScheduleQuotationsMap] = useState({});
  const [scheduleLoadingMap, setScheduleLoadingMap] = useState({});
  const [activeSchedule, setActiveSchedule] = useState(null);

  const navigate = useNavigate();
  const crmUser = JSON.parse(localStorage.getItem("crmUser"));

  useEffect(() => {
    if (id_contract) {
      getContract();
      getItems();
      getSchedules();
    }
  }, [id_contract]);

  const getContract = async () => {
    try {
      const fetchContract = await jwtFetch(config.ENDPOINT + "get/contract", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_contract: id_contract,
        }),
      });
      const responseGetContract = await fetchContract.json();
      if (responseGetContract.status === 200) {
        const contractData = responseGetContract.data[0];
        setContract(contractData);
        if (contractData.quotation_id) {
          getQuotationMaster(contractData.quotation_id);
        } else {
          setQuotationMaster(null);
        }
      } else {
        alert(responseGetContract.message);
      }
    } catch (error) {
      console.error('Error in getContract:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getQuotationMaster = async (quotationId) => {
    try {
      const fetchQuotation = await jwtFetch(config.ENDPOINT + "get/quotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: quotationId,
        }),
      });
      const responseGetQuotation = await fetchQuotation.json();
      if (responseGetQuotation.status === 200 && responseGetQuotation.data.length > 0) {
        setQuotationMaster(responseGetQuotation.data[0]);
      } else {
        setQuotationMaster({
          id_quotation: quotationId,
          quotation_code: null,
        });
      }
    } catch (error) {
      console.error('Error in getQuotationMaster:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getItems = async () => {
    try {
      const fetchItems = await jwtFetch(
        config.ENDPOINT + "get/contract_item",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contract_id: id_contract,
          }),
        },
      );
      const responseItems = await fetchItems.json();
      if (responseItems.status === 200) {
        setItems(responseItems.data);
      } else {
        alert(responseItems.message);
      }
    } catch (error) {
      console.error('Error in getItems:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getSchedules = async () => {
    try {
      const fetchSchedules = await jwtFetch(
        config.ENDPOINT + "get/contract_schedule",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contract_id: id_contract,
          }),
        },
      );
      const responseSchedules = await fetchSchedules.json();
      if (responseSchedules.status === 200) {
        setSchedules(responseSchedules.data);
      } else {
        alert(responseSchedules.message);
      }
    } catch (error) {
      console.error('Error in getSchedules:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const fetchScheduleQuotations = async (scheduleId) => {
    if (!scheduleId) return;
    setScheduleLoadingMap((p) => ({ ...p, [scheduleId]: true }));
    try {
      const res = await jwtFetch(config.ENDPOINT + "get/contract_schedule_quotations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_schedule_id: scheduleId }),
      });
      const j = await res.json();
      if (j.status === 200) {
        setScheduleQuotationsMap((p) => ({ ...p, [scheduleId]: j.data || [] }));
      } else {
        setScheduleQuotationsMap((p) => ({ ...p, [scheduleId]: [] }));
      }
    } catch (e) {
      setScheduleQuotationsMap((p) => ({ ...p, [scheduleId]: [] }));
    } finally {
      setScheduleLoadingMap((p) => ({ ...p, [scheduleId]: false }));
    }
  };

  const chooseActiveSchedule = (scheduleList) => {
    const now = new Date();
    const withDates = scheduleList
      .map((schedule) => {
        const start = schedule.schedule_start_date ? new Date(schedule.schedule_start_date) : null;
        const end = schedule.schedule_end_date ? new Date(schedule.schedule_end_date) : null;
        return { schedule, start, end };
      })
      .filter(({ start, end }) => start && end)
      .sort((a, b) => a.start - b.start);

    const current = withDates.find(({ start, end }) => start <= now && now <= end);
    if (current) {
      return String(current.schedule.id_contract_schedule);
    }

    const future = withDates.find(({ start }) => start > now);
    if (future) {
      return String(future.schedule.id_contract_schedule);
    }

    if (withDates.length > 0) {
      return String(withDates[withDates.length - 1].schedule.id_contract_schedule);
    }

    return scheduleList.length > 0 ? String(scheduleList[0].id_contract_schedule) : null;
  };

  const setDefaultActiveSchedule = async (scheduleList) => {
    const scheduleId = chooseActiveSchedule(scheduleList);
    if (scheduleId) {
      setActiveSchedule(scheduleId);
      if (!scheduleQuotationsMap[scheduleId]) {
        await fetchScheduleQuotations(scheduleId);
      }
    }
  };

  useEffect(() => {
    if (schedules.length > 0) {
      setDefaultActiveSchedule(schedules);
    }
  }, [schedules]);

  const handleScheduleSelect = async (selectedKey) => {
    setActiveSchedule(selectedKey);
    if (selectedKey && !scheduleQuotationsMap[selectedKey]) {
      await fetchScheduleQuotations(selectedKey);
    }
  };

  const getScheduleStatusLabel = (schedule) => {
    if (schedule.schedule_status !== "Active") {
      return schedule.schedule_status;
    }
    if (!schedule.schedule_start_date) {
      return schedule.schedule_status;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(schedule.schedule_start_date);
    startDate.setHours(0, 0, 0, 0);
    const dayDiff = differenceInCalendarDays(startDate, today);
    const hasEndDate = !!schedule.schedule_end_date;
    const quotationCount = Number(schedule.quotation_count || schedule.quotations || 0);
    let endDayDiff = null;
    if (hasEndDate) {
      const endDate = new Date(schedule.schedule_end_date);
      endDate.setHours(0, 0, 0, 0);
      endDayDiff = differenceInCalendarDays(endDate, today);
    }

    if (dayDiff < 0 && hasEndDate && endDayDiff < 0) {
      return <span className="text-danger">Expired</span>;
    }
    if (dayDiff < 0) {
      if (quotationCount > 0) {
        return "Active";
      }
      return <span className="text-danger">Overdue</span>;
    }
    if (dayDiff === 0) {
      if (quotationCount > 0) {
        return "Active";
      }
      return <span className="text-danger">Due</span>;
    }
    if (dayDiff <= 7) {
      return <span className="text-warning">Upcoming</span>;
    }
    return "Not Started";
  };

  return (
    <div id="contract-detail">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard">
                <img className="h-100" src="/cuancuan.png" alt="PointStar" />
              </a>
            </div>
            <hr className="m-0" />
            <Menu active="clients" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">
                {contract && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div className="fs-24">
                          <button className="btn btn-link" onClick={() => navigate(-1)}>
                            <i className="bi bi-arrow-left" />
                          </button>{" "}
                          Contract Detail
                        </div>
                        <span className={"badge badge-sm ms-3 text-bg-" + (contract.contract_status === "Active" ? "success" : "danger")}>{contract.contract_status}</span>
                      </div>
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => window.location.href = '/client/contract/edit/' + id_contract}><i className="bi bi-pencil me-1"/> Edit</button>
                    </div>
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Contract Name</div>
                          <div className="col-md-8">{contract.contract_name}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Company Group</div>
                          <div className="col-md-8">
                            {contract.company_name}{" "}
                            <a href={`/client/detail/${contract.company_id}`}>
                              <i className="bi bi-link-45deg" />
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Subsidiary</div>
                          <div className="col-md-8">
                            {contract.subcompany_name}{" "}
                            <a href={`/client/subsidiary/detail/${contract.subcompany_id}`}>
                              <i className="bi bi-link-45deg" />
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Created on</div>
                          <div className="col-md-8">{format(new Date(contract.created_on), 'd MMM yyyy')}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Sales Name</div>
                          <div className="col-md-8">{contract.sales_name}</div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        {contract.quotation_id && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between">
                                <p className="fs-14 mb-0">
                                  <strong>Quotation Master</strong>
                                </p>
                                <a href={`/quotation/detail/${contract.quotation_id}`}>
                                  {quotationMaster?.quotation_code || `Quotation #${contract.quotation_id}`}{" "}
                                  <i className="bi bi-link-45deg ms-1" />
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>                    
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">SKU List</div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-hover bordered mb-0">
                        <thead>
                          <tr>
                            <th className="bg-gold w-30">#</th>
                            <th className="bg-gold">SKUs</th>
                            <th className="bg-gold w-105">Type</th>
                            <th className="bg-gold w-105">Cost Price</th>
                            <th className="bg-gold w-105">Selling Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, i) => (
                            <tr className={item.product_total === 0 ? "d-none" : ""} key={i}>
                              <td valign="middle" align="center">{i + 1}</td>
                              <td valign="middle">{item.product_name}</td>
                              <td valign="middle">{item.product_type}</td>
                              <td align="right" valign="middle">
                                {priceFormat(item.product_cost_original, item.product_currency_original)}
                              </td>
                              <td align="right" valign="middle">
                                {priceFormat(item.product_price_original, item.product_currency_original)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {schedules && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Payment Schedules</div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-hover bordered mb-0">
                        <thead>
                          <tr>
                            <th className="bg-gold w-90">Schedule</th>
                            <th className="bg-gold w-200">Start Date</th>
                            <th className="bg-gold w-200">End Date</th>
                            <th className="bg-gold">Period</th>
                            <th className="bg-gold w-125">Status</th>
                            <th className="bg-gold">Quotations</th>
                            <th className="bg-gold w-125"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {schedules.map((schedule, i) => (
                            <tr key={i}>
                              <td valign="middle" align="center">{i + 1}</td>
                              <td valign="middle" align="center">{format(new Date(schedule.schedule_start_date), 'd MMM yyyy')}</td>
                              <td valign="middle" align="center">{format(new Date(schedule.schedule_end_date), 'd MMM yyyy')}</td>
                              <td valign="middle" align="center">{schedule.schedule_long_period + ' month(s)'}</td>
                              <td valign="middle" align="center">{getScheduleStatusLabel(schedule)}</td>
                              <td valign="middle" align="center">{schedule.quotation_count || schedule.quotations || 0}</td>
                              <td valign="middle" align="center"><a className="btn btn-icon btn-outline-secondary" href={`/client/contract/schedule/${schedule.id_contract_schedule}`}><i className="bi bi-eye"/></a></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Schedule Explorer: folder-like horizontal tabs */}
                    <div className="mt-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="fs-24">Schedule Explorer</div>
                      </div>

                      <Tab.Container activeKey={activeSchedule} onSelect={(k) => handleScheduleSelect(k)}>
                        <div className="row">
                          <div className="col-sm-3">
                            <Nav variant="pills" className="flex-column">
                              {schedules.map((s, idx) => (
                                <Nav.Item key={s.id_contract_schedule}>
                                  <Nav.Link className={`${activeSchedule === String(s.id_contract_schedule) ? 'bg-gold' : ''} text-dark`} eventKey={String(s.id_contract_schedule)}>
                                    <i className={`bi ${activeSchedule === String(s.id_contract_schedule) ? 'bi-folder2-open' : 'bi-folder'} me-2`} />
                                    Schedule {idx + 1}
                                  </Nav.Link>
                                </Nav.Item>
                              ))}
                            </Nav>
                          </div>
                          <div className="col-sm-9">
                            <Tab.Content>
                              {schedules.map((s) => (
                                <Tab.Pane eventKey={String(s.id_contract_schedule)} key={s.id_contract_schedule}>
                                  {activeSchedule === String(s.id_contract_schedule) && (
                                    <div>
                                      {/* quotations table for this schedule */}
                                      <div className="table-responsive">
                                        <table className="table table-hover bordered mb-0">
                                          <thead>
                                            <tr>
                                              <th className="bg-gold">#</th>
                                              <th className="bg-gold">Quotation Number</th>
                                              <th className="bg-gold">Status</th>
                                              <th className="bg-gold text-end">Amount</th>
                                              <th className="bg-gold">Effective Date</th>
                                              <th className="bg-gold"></th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {scheduleLoadingMap[s.id_contract_schedule] ? (
                                              <tr><td colSpan={7} className="text-center py-4">Loading...</td></tr>
                                            ) : (scheduleQuotationsMap[s.id_contract_schedule] && scheduleQuotationsMap[s.id_contract_schedule].length > 0 ? (
                                              scheduleQuotationsMap[s.id_contract_schedule].map((q, qi) => (
                                                <tr key={q.id_quotation}>
                                                  <td valign="middle">{qi + 1}</td>
                                                  <td valign="middle">{q.quotation_code}</td>
                                                  <td valign="middle" align="center">{q.quotation_status}</td>
                                                  <td valign="middle" align="right">{priceFormat(q.quotation_total, q.quotation_currency)}</td>
                                                  <td valign="middle" align="center">{q.created_on ? format(new Date(q.created_on), 'd MMM yyyy') : '-'}</td>
                                                  <td align="center"><a className="btn btn-sm btn-outline-secondary" href={`/quotation/detail/${q.id_quotation}`}><i className="bi bi-eye"/></a></td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr><td colSpan={7} className="text-center py-4 text-secondary">No quotations found.</td></tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </Tab.Pane>
                              ))}
                            </Tab.Content>
                          </div>
                        </div>
                      </Tab.Container>
                    </div>

                  </div>
                )}

                {contract && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Attachments</div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="row align-items-top form-group mb-2">
                          <div className="col-md-2">
                            <label>Files</label>
                          </div>
                          <div className="col-md-10">
                            {JSON.parse(contract.contract_attachments) && (
                              <div className="table-responsive">
                                <table className="table table-hover bordered mb-2">
                                  <thead>
                                    <tr>
                                      <th className="bg-gold w-30">#</th>
                                      <th className="bg-gold text-start">Name</th>
                                      <th className="bg-gold text-start">File</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {JSON.parse(contract.contract_attachments).map(
                                      (attachments, a) => (
                                        <tr key={"attachment-" + a}>
                                          <td valign="middle" align="center">
                                            {a + 1}
                                          </td>
                                          <td valign="middle">{attachments.name}</td>
                                          <td valign="middle">
                                            {Array.isArray(attachments.files) &&
                                              attachments.files.length > 0 && (
                                                <a
                                                  className="ellipsis"
                                                  href={attachments.files[0].fileUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {shortenUrl(attachments.files[0].fileUrl, 50)}{" "}
                                                  <i className="bi bi-link-45deg ms-2" />
                                                </a>
                                              )}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Remarks</label>
                          </div>
                          <div className="col-md-10">{contract.contract_remarks}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractDetail;


